import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * In-flight request de-duplication.
 *
 * A single user action (double-clicked button, impatient re-submit, a grid that
 * fires the same fetch from two effects) can produce several identical requests
 * before the first one answers. The registry below keeps one entry per distinct
 * request identity while it is on the wire; callers that ask for a request which
 * is already pending receive the promise of the pending one instead of putting a
 * second request on the wire.
 *
 * Sharing the pending promise - rather than dropping the duplicate - matters:
 * the caller's `then`/`catch` still runs, so modals close and toasts fire as the
 * component expects. The trade-off is that a success toast wired to the click
 * handler fires once per click; that is cosmetic, where a dropped promise would
 * leave the UI spinning forever.
 *
 * The registry is module scoped on purpose. `HttpClient()` builds a fresh axios
 * instance per call, so an instance-scoped registry would never see a duplicate.
 */

/**
 * Extra options understood by the guard. They ride along on a normal axios
 * config; axios ignores keys it does not know, so they never reach the wire.
 */
export interface RequestGuardConfig extends AxiosRequestConfig {
  /** Opt a single call out of de-duplication. */
  skipDedupe?: boolean;
  /** Replace the computed identity for this call. */
  dedupeKey?: string;
}

/**
 * Endpoints that must always be allowed to overlap with themselves. Both of
 * these currently bypass the guard anyway (they use the bare `axios` export
 * rather than the wrapped instance), but they are listed so that moving them
 * onto `HttpClient` later cannot silently break them.
 */
const DEDUPE_DENYLIST: RegExp[] = [
  // Fire-and-forget telemetry, sent once per request - deduping it would drop rows.
  /EndpointMapping\/RecordMapping/i,
  // Token refresh has its own single-flight guard in HttpClient.
  /Account\/RefreshToken/i,
];

const inFlight = new Map<string, Promise<AxiosResponse<unknown>>>();

const describeFile = (value: File): string =>
  `File(${value.name}:${value.size}:${value.type}:${value.lastModified})`;

/**
 * Serialises a payload to a stable string.
 *
 * Object keys are sorted so two payloads that differ only in key order share an
 * identity. Values that cannot be read synchronously (files, blobs, buffers) are
 * reduced to their descriptive metadata, which is enough to tell two different
 * uploads apart. `seen` tracks the current path only - it is unwound after each
 * branch so that a repeated sibling reference serialises in full and only a true
 * cycle collapses.
 */
const stableSerialize = (
  value: unknown,
  seen: WeakSet<object> = new WeakSet()
): string => {
  if (value === null || value === undefined) return String(value);

  const valueType = typeof value;
  if (
    valueType === "string" ||
    valueType === "number" ||
    valueType === "boolean"
  ) {
    return JSON.stringify(value);
  }
  if (valueType === "bigint") return `${value as bigint}n`;
  if (valueType === "function" || valueType === "symbol") return `[${valueType}]`;

  if (typeof File !== "undefined" && value instanceof File)
    return describeFile(value);
  if (typeof Blob !== "undefined" && value instanceof Blob)
    return `Blob(${value.size}:${value.type})`;
  if (value instanceof Date) return `Date(${value.getTime()})`;
  if (typeof FormData !== "undefined" && value instanceof FormData) {
    const parts: string[] = [];
    value.forEach((entry, key) => {
      parts.push(`${key}=${stableSerialize(entry, seen)}`);
    });
    return `FormData{${parts.join("&")}}`;
  }
  if (
    typeof URLSearchParams !== "undefined" &&
    value instanceof URLSearchParams
  ) {
    return `Params{${value.toString()}}`;
  }
  if (value instanceof ArrayBuffer) return `ArrayBuffer(${value.byteLength})`;
  if (ArrayBuffer.isView(value)) return `TypedArray(${value.byteLength})`;

  const objectValue = value as Record<string, unknown>;
  if (seen.has(objectValue)) return "[circular]";
  seen.add(objectValue);

  let serialized: string;
  if (Array.isArray(value)) {
    serialized = `[${value
      .map((item) => stableSerialize(item, seen))
      .join(",")}]`;
  } else {
    serialized = `{${Object.keys(objectValue)
      .sort()
      .map((key) => `${key}:${stableSerialize(objectValue[key], seen)}`)
      .join(",")}}`;
  }

  seen.delete(objectValue);
  return serialized;
};

/**
 * Builds the request identity. The full serialised payload is used rather than a
 * hash of it: entries live only for the duration of the request, so the registry
 * holds a handful at a time, and a full key removes any chance that two large
 * payloads differing in one field collapse onto the same entry.
 */
const buildKey = (
  method: string,
  url: string,
  data: unknown,
  config?: RequestGuardConfig
): string => {
  if (config?.dedupeKey) return config.dedupeKey;
  return [
    method.toUpperCase(),
    config?.baseURL ?? "",
    url,
    stableSerialize(config?.params ?? null),
    stableSerialize(data ?? null),
  ].join("|");
};

const isDedupable = (url: string, config?: RequestGuardConfig): boolean => {
  if (config?.skipDedupe) return false;
  return !DEDUPE_DENYLIST.some((pattern) => pattern.test(url));
};

const share = <T>(key: string, start: () => Promise<T>): Promise<T> => {
  const existing = inFlight.get(key);
  if (existing) return existing as unknown as Promise<T>;

  const pending = start().finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, pending as unknown as Promise<AxiosResponse<unknown>>);
  return pending;
};

const URL_VERBS = ["get", "delete", "head", "options"] as const;
const DATA_VERBS = ["post", "put", "patch"] as const;

/**
 * Swapping axios's own verb helpers out for wrapped versions needs one escape
 * hatch from their declared types. It is confined to this alias.
 */
type PatchableInstance = Record<string, (...args: any[]) => unknown>;

/**
 * Wraps an axios instance so every verb consults the shared registry.
 *
 * Each verb is wrapped individually rather than wrapping `request`: axios binds
 * its verb helpers to an internal context, so `instance.post` calls that
 * context's `request` and never sees an override placed on `instance.request`.
 */
export const withRequestGuard = (instance: AxiosInstance): AxiosInstance => {
  const patchable = instance as unknown as PatchableInstance;

  URL_VERBS.forEach((verb) => {
    const original = instance[verb].bind(instance);
    patchable[verb] = (url: string, config?: RequestGuardConfig) => {
      if (!isDedupable(url, config)) return original(url, config);
      return share(buildKey(verb, url, undefined, config), () =>
        original(url, config)
      );
    };
  });

  DATA_VERBS.forEach((verb) => {
    const original = instance[verb].bind(instance);
    patchable[verb] = (
      url: string,
      data?: unknown,
      config?: RequestGuardConfig
    ) => {
      if (!isDedupable(url, config)) return original(url, data, config);
      return share(buildKey(verb, url, data, config), () =>
        original(url, data, config)
      );
    };
  });

  const originalRequest = instance.request.bind(instance);
  patchable.request = (config: RequestGuardConfig = {}) => {
    const url = config.url ?? "";
    if (!isDedupable(url, config)) return originalRequest(config);
    return share(
      buildKey(config.method ?? "get", url, config.data, config),
      () => originalRequest(config)
    );
  };

  return instance;
};

/** Number of requests currently on the wire. Diagnostics and tests. */
export const getInFlightCount = (): number => inFlight.size;

/** Drops every registry entry. Tests and hard resets (logout) only. */
export const clearInFlightRequests = (): void => {
  inFlight.clear();
};
